import { useMemo } from 'react';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { DarwinListView } from 'components/darwin-ui';
import { getMassiveTransactionsColumns } from './columns';
import useTableControls from 'hooks/useTableControls';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useGetMassiveTransactions } from 'api/services/massiveTransactionsService';
import { MassiveTransaction } from 'types/massiveTransactionTypes';
import { useUI } from 'contexts/UiContext';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';

export const UploadHistory = () => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const navigate = useNavigate();
  const { selectedCompany } = useUI();

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    sortOrder,
    setSearchQuery,
    onTableChange,
  } = useTableControls({
    defaultSortBy: 'createdAt',
    defaultSortOrder: 'DESC',
  });

  const {
    data: transactionsData,
    isLoading,
    isFetching,
    isPending,
    error: transactionsError,
  } = useGetMassiveTransactions({
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
    companyId: selectedCompany || '',
  });

  // Show error notification
  if (transactionsError?.message) {
    openNotification('Error', t('upload-history.loading-error'), 'error');
  }

  const tableData = useMemo(
    () => transactionsData?.data ?? [],
    [transactionsData]
  );
  const loading = isLoading || isFetching || isPending;

  const columns = useMemo(
    () => getMassiveTransactionsColumns(t, searchQuery),
    [t, searchQuery]
  );

  return (
    <>
      <ContentHeader title={t('upload-history.title')} />
      <DarwinListView<MassiveTransaction>
        data={tableData}
        columns={columns}
        isLoading={loading}
        header={{
          setSearchQuery,
          searchPlaceholder: t('upload-history.search-placeholder'),
        }}
        table={{ onChange: onTableChange }}
        behavior={{
          onRowClick: processData =>
            navigate(
              generatePath(ROUTES.ERROR_DETAILS.LIST, {
                id: processData.id,
              }),
              { state: processData }
            ),
        }}
        pagination={{
          total: transactionsData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
      />
    </>
  );
};
